import { OptimizerRunV2Mode, OptimizerRunV2State } from '@generated/graphql';
import type { OptimizerRunV2PortfolioSummary } from '../../types/run.types';

const RUN_STATE_PROGRESS: Record<OptimizerRunV2State, number> = {
  [OptimizerRunV2State.Initiated]: 10,
  [OptimizerRunV2State.Optimizing]: 45,
  [OptimizerRunV2State.Validating]: 70,
  [OptimizerRunV2State.Publishing]: 90,
  [OptimizerRunV2State.Completed]: 100,
  [OptimizerRunV2State.Failed]: 100,
};

export const getProcessedPortfolioCount = (
  summary: OptimizerRunV2PortfolioSummary | null | undefined,
): number =>
  (summary?.succeeded ?? 0) + (summary?.failed ?? 0) + (summary?.skipped ?? 0);

export const getRunProgressValue = (
  state: OptimizerRunV2State,
  summary?: OptimizerRunV2PortfolioSummary | null,
): number => {
  if (state === OptimizerRunV2State.Completed) {
    return 100;
  }

  if (state === OptimizerRunV2State.Failed) {
    return 100;
  }

  const stateProgress = RUN_STATE_PROGRESS[state] ?? 0;

  if (summary && summary.total > 0) {
    const processed = getProcessedPortfolioCount(summary);
    const ratioProgress = Math.round((processed / summary.total) * 95);

    if (processed > 0) {
      return Math.max(ratioProgress, Math.min(stateProgress, 94));
    }
  }

  return stateProgress;
};

export const isRunInProgress = (state: OptimizerRunV2State): boolean =>
  state !== OptimizerRunV2State.Completed &&
  state !== OptimizerRunV2State.Failed;

/** Live portfolio report holdings override audit reconstruction only while a run is active. */
export const shouldMergeLiveReportHoldings = (
  state: OptimizerRunV2State,
): boolean => isRunInProgress(state);

export const canShowRunReports = (state: OptimizerRunV2State): boolean =>
  state === OptimizerRunV2State.Completed ||
  state === OptimizerRunV2State.Failed ||
  isRunInProgress(state);

export const usesOptimizedPortfolioReportsQuery = (
  state: OptimizerRunV2State,
): boolean => state === OptimizerRunV2State.Completed;

export const canLoadRunAudits = (state: OptimizerRunV2State): boolean =>
  state === OptimizerRunV2State.Validating ||
  state === OptimizerRunV2State.Publishing ||
  state === OptimizerRunV2State.Completed ||
  state === OptimizerRunV2State.Failed;

export const TERMINAL_RUN_STATES: OptimizerRunV2State[] = [
  OptimizerRunV2State.Completed,
  OptimizerRunV2State.Failed,
];

export const canProceedToBulkTrader = (
  state: OptimizerRunV2State,
  mode: OptimizerRunV2Mode,
  succeededCount: number,
): boolean =>
  state === OptimizerRunV2State.Completed &&
  mode === OptimizerRunV2Mode.Live &&
  succeededCount > 0;
